import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOrganizerPlayers, createPlayerByOrganizer, updateUserProfile, getEvents, registerToEvent, getEventRegistrations, deletePlayer } from '../../services/api';
import PlayerForm from '../../components/player/PlayerForm';
import toast from 'react-hot-toast';

const ManagePlayers = () => {
    const userId = localStorage.getItem('userId');
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
    const [enrollFormData, setEnrollFormData] = useState({
        playerId: '',
        eventId: '',
        paymentMethod: 'efectivo',
        paymentStatus: 'pagado'
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [showResults, setShowResults] = useState(false);

    const { data, isLoading, error } = useQuery({
        queryKey: ['organizer-players', userId],
        queryFn: () => getOrganizerPlayers(userId),
        enabled: !!userId
    });

    const { data: eventsData } = useQuery({
        queryKey: ['organizer-events', userId],
        queryFn: () => getEvents({ organizerId: userId }),
        enabled: !!userId
    });

    const events = eventsData?.events || [];

    const mutation = useMutation({
        mutationFn: (playerData) => {
            if (selectedPlayer?.id) {
                return updateUserProfile(selectedPlayer.id, playerData);
            } else {
                return createPlayerByOrganizer({ ...playerData, organizerId: userId });
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['organizer-players', userId]);
            toast.success(selectedPlayer ? 'Perfil actualizado' : 'Jugador registrado');
            handleCloseModal();
        },
        onError: (err) => {
            console.error("Error saving player:", err);
            toast.error('Ocurrió un error al guardar');
        }
    });

    if (error) console.error('[ManagePlayers] Query Error:', error);

    const players = data?.players || [];

    const filteredPlayers = players.filter(p => 
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const enrollMutation = useMutation({
        mutationFn: async (enrollData) => {
            // Check for duplicates
            const existingRegs = await getEventRegistrations(enrollData.eventId);
            const isAlreadyRegistered = existingRegs.some(r => 
                r.player1Id === enrollData.playerId || r.player2Id === enrollData.playerId
            );

            if (isAlreadyRegistered) {
                throw new Error('EL_JUGADOR_YA_ESTA_INSCRITO');
            }

            // Find player name to pass to registration
            const player = players.find(p => p.id === enrollData.playerId);
            const pName = player ? `${player.firstName} ${player.lastName}`.trim() : 'Jugador';

            return registerToEvent(enrollData.eventId, {
                player1Id: enrollData.playerId,
                player1Name: pName, // Critical: Pass the player's name instead of the organizer's
                status: 'confirmed',
                isManualRegistration: true,
                paymentMethod: enrollData.paymentMethod,
                paymentStatus: enrollData.paymentStatus,
                recordedByOrganizer: userId,
                priceSnapshot: 0 
            });
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries(['event-registrations', variables.eventId]);
            queryClient.invalidateQueries(['my-events', userId]);
            queryClient.invalidateQueries(['organizer-players', userId]);
            toast.success('Inscripción realizada con éxito');
            setIsEnrollModalOpen(false);
            setEnrollFormData({ playerId: '', eventId: '', paymentMethod: 'efectivo', paymentStatus: 'pagado' });
            setSearchTerm('');
        },
        onError: (err) => {
            if (err.message === 'EL_JUGADOR_YA_ESTA_INSCRITO' || err.response?.data?.message === 'EL_JUGADOR_YA_ESTA_INSCRITO') {
                toast.error('Este jugador ya está inscrito en este pozo');
            } else {
                console.error("Error enrolling player:", err);
                toast.error('Error al realizar la inscripción');
            }
        }
    });

    const deletePlayerMutation = useMutation({
        mutationFn: (id) => deletePlayer(id),
        onSuccess: () => {
            queryClient.invalidateQueries(['organizer-players', userId]);
            toast.success('Jugador eliminado del registro');
        },
        onError: (err) => {
            console.error("Error deleting player:", err);
            toast.error('No se pudo eliminar al jugador');
        }
    });

    const handleDeletePlayer = (player) => {
        const confirm1 = window.confirm(`¿Estás seguro de que deseas eliminar a ${player.firstName} ${player.lastName} de tu base de datos?`);
        if (confirm1) {
            const confirm2 = window.confirm(`ATENCIÓN: Se borrarán sus datos de contacto y perfil. Esta acción no se puede deshacer. ¿Confirmar eliminación definitiva?`);
            if (confirm2) {
                deletePlayerMutation.mutate(player.id);
            }
        }
    };

    const handleOpenModal = (player = null) => {
        setSelectedPlayer(player);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setSelectedPlayer(null);
        setIsModalOpen(false);
    };

    const handleOpenEnrollModal = (player = null) => {
        if (player) {
            setEnrollFormData(prev => ({ ...prev, playerId: player.id }));
            setSearchTerm(`${player.firstName} ${player.lastName}`);
        } else {
            setEnrollFormData({ playerId: '', eventId: '', paymentMethod: 'efectivo', paymentStatus: 'pagado' });
            setSearchTerm('');
        }
        setIsEnrollModalOpen(true);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-1 sm:mb-2">Mis Jugadores</h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">Base de datos de jugadores que han participado en tus pozos.</p>
                </div>
                <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2">
                    <button 
                        onClick={() => handleOpenEnrollModal()}
                        className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-black py-3 px-6 rounded-xl transition-all shadow-lg shadow-slate-900/10 flex items-center justify-center gap-2 uppercase italic text-[10px] tracking-widest"
                    >
                        <span>🏟️</span> Inscribir en Pozo
                    </button>
                    <button 
                        onClick={() => handleOpenModal()}
                        className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-black py-3 px-6 rounded-xl transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 uppercase italic text-[10px] tracking-widest"
                    >
                        <span>➕</span> Nuevo Jugador
                    </button>
                </div>
            </div>

            {/* Vista Desktop: Tabla */}
            <div className="hidden md:block bg-white dark:bg-slate-800 rounded-2xl lg:rounded-[2.5rem] border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden transition-colors">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-slate-700/50 text-gray-400 dark:text-gray-500 text-xs font-black uppercase tracking-widest">
                            <tr>
                                <th className="px-6 py-4">Jugador</th>
                                <th className="px-6 py-4">Email</th>
                                <th className="px-6 py-4">Nivel</th>
                                <th className="px-6 py-4">Último Evento</th>
                                <th className="px-6 py-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50 text-sm">
                            {isLoading ? (
                                [1, 2, 3].map(i => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan="5" className="px-6 py-10 bg-gray-50/30 dark:bg-slate-700/30"></td>
                                    </tr>
                                ))
                            ) : players.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-20 text-center text-gray-500 font-bold">
                                        Aún no tienes jugadores registrados en tus eventos.
                                    </td>
                                </tr>
                            ) : players.map((player) => (
                                <tr key={player.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 font-black">
                                                {player.firstName.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="font-black text-gray-900 dark:text-white uppercase tracking-tight">
                                                    {player.firstName} {player.lastName}
                                                </div>
                                                <div className="text-[10px] text-gray-400 dark:text-gray-500 font-bold">ID: {player.id.substring(0, 8)}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-sm font-semibold text-gray-600 dark:text-gray-400">
                                        {player.email}
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className="bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 font-black px-2.5 py-1 rounded-lg text-xs border border-primary-100 dark:border-primary-500/20">
                                            {player.level}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 text-sm font-bold text-gray-500 dark:text-gray-400">
                                        {player.lastEvent}
                                    </td>
                                    <td className="px-6 py-5 text-right flex justify-end gap-3">
                                        <button 
                                            onClick={() => handleOpenEnrollModal(player)}
                                            className="bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-900 dark:hover:bg-slate-600 hover:text-white px-3 py-1.5 rounded-lg transition-all font-black text-[10px] uppercase tracking-wider border border-slate-100 dark:border-slate-600"
                                        >
                                            Inscribir
                                        </button>
                                        <button 
                                            onClick={() => handleOpenModal(player)}
                                            className="text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-bold text-sm"
                                        >
                                            Editar
                                        </button>
                                        <button 
                                            onClick={() => handleDeletePlayer(player)}
                                            className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors font-bold text-sm"
                                            title="Eliminar Jugador"
                                        >
                                            Borrar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Vista Mobile: Cards */}
            <div className="md:hidden space-y-4">
                {isLoading ? (
                    [1, 2, 3].map(i => (
                        <div key={i} className="bg-white dark:bg-slate-800 rounded-2xl p-4 animate-pulse h-24"></div>
                    ))
                ) : players.length === 0 ? (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 text-center text-gray-500 dark:text-gray-400 font-bold border border-gray-100 dark:border-slate-700">
                        Aún no tienes jugadores registrados en tus eventos.
                    </div>
                ) : players.map((player) => (
                    <div key={player.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm p-4 transition-colors">
                        <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-400 font-black flex-shrink-0">
                                {player.firstName.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-black text-gray-900 dark:text-white uppercase tracking-tight truncate text-sm">
                                            {player.firstName} {player.lastName}
                                        </h3>
                                        <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold truncate mt-0.5">ID: {player.id}</p>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{player.email}</p>
                                    </div>
                                    <span className="bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 font-black px-2 py-1 rounded-lg text-[10px] border border-primary-100 dark:border-primary-500/20 flex-shrink-0">
                                        {player.level}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                                    <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest truncate max-w-[120px]">{player.lastEvent}</span>
                                    <div className="flex gap-4 items-center">
                                        <button 
                                            onClick={() => handleOpenModal(player)}
                                            className="text-primary-600 dark:text-primary-400 font-bold text-xs hover:text-primary-700 dark:hover:text-primary-300"
                                        >
                                            Editar
                                        </button>
                                        <button 
                                            onClick={() => handleDeletePlayer(player)}
                                            className="text-red-500 dark:text-red-400 font-bold text-xs hover:text-red-700 dark:hover:text-red-300"
                                        >
                                            Borrar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal de Ficha de Jugador */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-sm animate-fade-in" onClick={handleCloseModal}>
                    <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-slide-up transition-colors" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="p-6 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50">
                             <div>
                                 <h3 className="font-black text-gray-900 dark:text-white uppercase tracking-widest italic text-lg">
                                     {selectedPlayer ? 'Editar Ficha Jugador' : 'Registrar Nuevo Jugador'}
                                 </h3>
                                 <p className="text-xs text-gray-400 dark:text-gray-500 font-bold uppercase tracking-wider mt-1">
                                     {selectedPlayer ? 'Actualiza los datos y preferencias' : 'Completa los campos para dar de alta al jugador'}
                                 </p>
                             </div>
                             <button onClick={handleCloseModal} className="w-10 h-10 rounded-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-white transition-all hover:rotate-90 shadow-sm">
                                 <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                             </button>
                        </div>

                        {/* Form Body */}
                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                             <PlayerForm 
                                 initialData={selectedPlayer || {}} 
                                 onSubmit={mutation.mutate} 
                                 isSubmitting={mutation.isPending} 
                             />
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Inscripción en Pozo */}
            {isEnrollModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-sm animate-fade-in" onClick={() => setIsEnrollModalOpen(false)}>
                    <div className="bg-white dark:bg-slate-800 rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden animate-slide-up transition-colors" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 flex justify-between items-center">
                            <div>
                                <h3 className="font-black text-gray-900 dark:text-white uppercase tracking-widest italic text-lg">Inscribir en Pozo</h3>
                                <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase mt-1">Gestión directa de plazas</p>
                            </div>
                            <button onClick={() => setIsEnrollModalOpen(false)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-white transition-all">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        
                        <div className="p-8 space-y-6">
                            {/* Selector de Jugador con Buscador */}
                             <div className="relative">
                                <label className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Buscar Jugador</label>
                                <div className="relative">
                                    <input 
                                        type="text"
                                        className="w-full px-5 py-3.5 rounded-2xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all pl-12 dark:text-white dark:placeholder-gray-600"
                                        placeholder="Nombre, apellido o email..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setShowResults(true);
                                            if (enrollFormData.playerId) setEnrollFormData(p => ({ ...p, playerId: '' }));
                                        }}
                                        onFocus={() => setShowResults(true)}
                                    />
                                    <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">🔍</span>
                                </div>

                                 {showResults && searchTerm && !enrollFormData.playerId && (
                                    <div className="absolute z-50 left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-700 max-h-60 overflow-y-auto overflow-hidden animate-slide-down">
                                        {filteredPlayers.length > 0 ? (
                                            filteredPlayers.map(p => (
                                                <button
                                                    key={p.id}
                                                    onClick={() => {
                                                        setEnrollFormData(prev => ({ ...prev, playerId: p.id }));
                                                        setSearchTerm(`${p.firstName} ${p.lastName}`);
                                                        setShowResults(false);
                                                    }}
                                                    className="w-full px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-between group"
                                                >
                                                    <div>
                                                        <div className="font-black text-gray-900 dark:text-white uppercase text-xs tracking-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                                            {p.firstName} {p.lastName}
                                                        </div>
                                                        <div className="text-[10px] text-gray-400 dark:text-gray-500 font-bold">{p.email}</div>
                                                    </div>
                                                    <span className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-[10px] font-black px-2 py-1 rounded-lg uppercase">Seleccionar</span>
                                                </button>
                                            ))
                                        ) : (
                                            <div className="p-6 text-center">
                                                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold mb-4">No se encontraron jugadores</p>
                                                <button 
                                                    onClick={() => {
                                                        setIsEnrollModalOpen(false);
                                                        handleOpenModal();
                                                    }}
                                                    className="bg-blue-600 text-white text-[10px] font-black px-4 py-2 rounded-xl uppercase tracking-widest hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
                                                >
                                                    ➕ Registrar Nuevo Jugador
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                             {/* Selector de Pozo */}
                            <div>
                                <label className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Seleccionar Pozo / Evento</label>
                                <select 
                                    className="w-full px-5 py-3.5 rounded-2xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all dark:text-white"
                                    value={enrollFormData.eventId}
                                    onChange={(e) => setEnrollFormData(prev => ({ ...prev, eventId: e.target.value }))}
                                >
                                    <option value="">Selecciona un pozo activo...</option>
                                    {events.map(ev => (
                                        <option key={ev.id} value={ev.id}>{ev.title} - {new Date(ev.date).toLocaleDateString()}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Pago */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Forma de Pago</label>
                                    <select 
                                        className="w-full px-5 py-3.5 rounded-2xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer dark:text-white"
                                        value={enrollFormData.paymentMethod}
                                        onChange={(e) => setEnrollFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                                    >
                                        <option value="efectivo">💶 Efectivo</option>
                                        <option value="bizum">📱 Bizum</option>
                                        <option value="tarjeta">💳 Tarjeta (App)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Estado</label>
                                    <select 
                                        className="w-full px-5 py-3.5 rounded-2xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer dark:text-white"
                                        value={enrollFormData.paymentStatus}
                                        onChange={(e) => setEnrollFormData(prev => ({ ...prev, paymentStatus: e.target.value }))}
                                    >
                                        <option value="pagado">✅ Pagado</option>
                                        <option value="pendiente">⏳ Pendiente</option>
                                    </select>
                                </div>
                            </div>

                            <button 
                                onClick={() => enrollMutation.mutate(enrollFormData)}
                                disabled={enrollMutation.isPending || !enrollFormData.playerId || !enrollFormData.eventId}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl transition-all shadow-xl shadow-blue-500/20 disabled:opacity-50 uppercase italic text-sm tracking-widest flex items-center justify-center gap-3"
                            >
                                {enrollMutation.isPending ? 'Procesando...' : 'Confirmar Inscripción'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManagePlayers;
